import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getPaymentDetails, checkPaymentStatus, cancelPayment } from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";


export default function PaymentPage() {
  const { id } = useParams();
  const [paymentData, setPaymentData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [status, setStatus] = useState("pending");

  // Load payment details
  useEffect(() => {
    const fetchPayment = async () => {
      try {
        const res = await getPaymentDetails(id);
        if (res.success) {
          const data = res.data;
          setPaymentData(data);
          setStatus(data.status);
          const expiry = new Date(data.expiry_time).getTime();
          const now = Date.now();
          const diff = Math.max(0, Math.floor((expiry - now) / 1000));
          setTimeLeft(diff);

          if (diff <= 0) setStatus("expired");
        } else {
          setStatus("failed");
        }
      } catch (err) {
        console.error("Error loading payment:", err);
        setStatus("failed");
      }
    };
    fetchPayment();
  }, [id]);

  // Countdown
useEffect(() => {
  if (timeLeft === null) return;

  const timer = setInterval(() => {
    setTimeLeft((t) => {
      if (t <= 1) {
        clearInterval(timer);

        setStatus("expired");
        toast.error("⏰ Session Expired. Redirecting...", { autoClose: 2000 });

        if (paymentData?.redirect_url) {
          window.location.href = paymentData.redirect_url;
        }

        return 0;
      }
      return t - 1;
    });
  }, 1000);

  return () => clearInterval(timer);
}, [timeLeft, paymentData]);


  // Poll payment status
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await checkPaymentStatus(paymentData.order_id);
        console.log(res);
        if (res.status === "Complete") {
          setStatus("success");
          toast.success("✅ Payment Successful!", { autoClose: 2000 });
          setTimeout(() => {
            window.location.href = paymentData.redirect_url;
          }, 1000);
        } else if (res.status === "FAILED") {
          setStatus("failed");
          toast.error("❌ Payment Failed!", { autoClose: 2000 });
          setTimeout(() => {
            window.location.href = paymentData.redirect_url;
          }, 1000);
        } else if (res.status === "cancelled") {
          setStatus("cancelled");
          toast.warn("⚠️ Payment Cancelled!", { autoClose: 2000 });
          setTimeout(() => {
            window.location.href = paymentData.redirect_url;
          }, 1000);
        }
      } catch (err) {
        console.error("Error checking status:", err);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [status, paymentData]);


  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  if (!paymentData && status === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-700">
        Loading payment details...
      </div>
    );
  }

  return (
   <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4" >
   <div className="bg-white max-w-[400px] rounded-xl shadow-lg p-6 text-center p-4" style={{fontSize: "15px"}}>
    <ToastContainer position="top-center" />
    {/* Header */}
    <div className="flex flex-col items-center mb-6">
      <img
        src={paymentData?.company_logo || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ1z3WO2y5h7YkHljxIsvwuOxP21OE_8tnedA&s"}
        alt="Company Logo"
        className="h-16 rounded-full shadow mb-2" style={{maxWidth: "60px", marginRight: "10px"}}
      />
      <h5 className="font-bold text-lg">
        {paymentData?.company_name || "MyPay Gateway"}
      </h5>
    </div>

    {/* Payment Info */}
    <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
      <div className="flex justify-between mb-2">
        <span className="text-gray-500 text-sm">Amount:</span>
        <span className="font-semibold text-green-600">
          ${paymentData?.amount || "0.00"}
        </span>
      </div>
      <div className="flex justify-between mb-2">
        <span className="text-gray-500 text-sm mr-3">Order ID:</span>
        <span className="font-mono text-xs" style={{marginLeft: "20px"}}>{paymentData?.order_id}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 text-sm">Network:</span>
        <span className="font-mono text-xs">{paymentData?.chain || "USDT"}</span>
      </div>
    </div>

    {/* QR Code */}
    <div className="flex flex-col items-center mb-6" style={{marginLeft: "40px"}}>
      <img
        src={paymentData?.qr_code}
        alt="QR Code"
        className="w-48 h-48 rounded-lg border mb-3"
      />
    </div>

    {/* Expiry Timer */}
    <div className="mb-6">
      <p className="text-red-600 font-semibold">
        Expires in: {timeLeft !== null ? formatTime(timeLeft) : "--:--"}
      </p>
    </div>

    {/* Address with Copy */}
    <div className="flex items-center border rounded-lg overflow-hidden mb-6">
      <input
        type="text"
        readOnly
        value={paymentData?.payment_link}
        className="flex-1 px-2 py-2 text-xs bg-gray-50 text-center focus:outline-none"
        onClick={(e) => e.target.select()}
      />
      <button
  onClick={() => {
    navigator.clipboard.writeText(paymentData?.payment_link);
    alert("Copied to clipboard!");
  }}
  className="text-white text-xs" style={{background: "blue"}}
>
  Copy
</button>
    </div>

    {/* Cancel Button */}
    <button
  onClick={async () => {
    if (!paymentData) return;
    const res = await cancelPayment(paymentData.order_id);
    if (res.success) {
      setStatus("cancelled");
      alert("Payment cancelled.");
      window.location.href = paymentData.redirect_url; // redirect after cancel
    } else {
      alert("Failed to cancel payment");
    }
  }}
  className="bg-red-600 hover:bg-red-700 text-white w-full py-3 rounded-lg font-semibold" style={{background: "blue"}}
>
  Cancel
</button>
  </div>
</div>





  );
}
